import type {
  WorkspaceEdge,
  WorkspaceNode,
  WorkspaceNodeStatus,
  WorkspaceNodeType,
} from "../../../types/workspace";
import {
  normalizeReportTypeKey,
  resolveReportTypeDisplayLabel,
} from "../../../utils/reportTypeRules";
import { listExecutingAccountBridges } from "./layoutStrategyGraphSequential";
import { workspaceNodeIsSimulationRerun, workspaceNodeTitle } from "./workspaceGraphUtils";

/** Toolbar type options — order matches canvas pipeline columns. */
export const WORKSPACE_FOCUS_TYPE_GROUPS = [
  { id: "strategy", label: "Strategies", types: ["strategy"] as const },
  { id: "report", label: "Reports", types: ["report"] as const },
  {
    id: "simulation",
    label: "Simulations",
    types: ["simulation", "combined_simulation"] as const,
  },
  {
    id: "policy",
    label: "Policies",
    // Match Simulations: plain + combined share one filter (Sequential exec arms
    // usually hang off combined_policy).
    types: ["policy", "combined_policy"] as const,
  },
] as const;

export type WorkspaceFocusTypeGroupId = (typeof WORKSPACE_FOCUS_TYPE_GROUPS)[number]["id"];

const LEGACY_FOCUS_GROUP_ALIASES: Partial<
  Record<string, WorkspaceFocusTypeGroupId>
> = {
  // Older filters lumped combined sims under Simulations.
  combined_simulation: "simulation",
  // Older filters had a separate Combined policies group.
  combined_policy: "policy",
};

function normalizeFocusGroupId(groupId: string): WorkspaceFocusTypeGroupId | null {
  const aliased = LEGACY_FOCUS_GROUP_ALIASES[groupId];
  if (aliased) return aliased;
  if (WORKSPACE_FOCUS_TYPE_GROUPS.some((group) => group.id === groupId)) {
    return groupId as WorkspaceFocusTypeGroupId;
  }
  return null;
}

export type WorkspaceSimulationFocusKind = "any" | "original" | "rerun";

/** How non-matching nodes are treated when a focus filter is active. */
export type WorkspaceFocusVisibilityMode = "dim" | "hide";

export type WorkspaceFocusFilter = {
  /** Empty = show everything at full strength. */
  groupIds: WorkspaceFocusTypeGroupId[];
  /** Optional free-text match on title / subtitle / enrichment labels. */
  query: string;
  /** When set, matching nodes must have one of these statuses (nodes without status are excluded). */
  statuses: WorkspaceNodeStatus[];
  /** Report-type keys (normalized). Empty = any report type. */
  reportTypes: string[];
  /** Restrict simulations to originals, reruns, or either. */
  simulationKind: WorkspaceSimulationFocusKind;
  /** Dim unmatched nodes (default) or remove them from the canvas. */
  visibilityMode: WorkspaceFocusVisibilityMode;
};

export const EMPTY_WORKSPACE_FOCUS_FILTER: WorkspaceFocusFilter = {
  groupIds: [],
  query: "",
  statuses: [],
  reportTypes: [],
  simulationKind: "any",
  visibilityMode: "dim",
};

/** Per-entity enrichment used by deeper focus filters. */
export type WorkspaceFocusEnrichment = {
  reportTypeByEntityId: Record<string, string>;
};

export const EMPTY_WORKSPACE_FOCUS_ENRICHMENT: WorkspaceFocusEnrichment = {
  reportTypeByEntityId: {},
};

export function isWorkspaceFocusFilterActive(filter: WorkspaceFocusFilter): boolean {
  return (
    filter.groupIds.length > 0 ||
    filter.query.trim().length > 0 ||
    filter.statuses.length > 0 ||
    filter.reportTypes.length > 0 ||
    filter.simulationKind !== "any"
  );
}

export function workspaceFocusFilterHasRefinements(filter: WorkspaceFocusFilter): boolean {
  return (
    filter.query.trim().length > 0 ||
    filter.statuses.length > 0 ||
    filter.reportTypes.length > 0 ||
    filter.simulationKind !== "any"
  );
}

export function typesForFocusGroups(
  groupIds: readonly (WorkspaceFocusTypeGroupId | string)[]
): Set<WorkspaceNodeType> {
  const types = new Set<WorkspaceNodeType>();
  const normalizedGroupIds = new Set<WorkspaceFocusTypeGroupId>();
  for (const groupId of groupIds) {
    const normalized = normalizeFocusGroupId(groupId);
    if (normalized) normalizedGroupIds.add(normalized);
  }
  for (const group of WORKSPACE_FOCUS_TYPE_GROUPS) {
    if (!normalizedGroupIds.has(group.id)) continue;
    for (const type of group.types) types.add(type);
  }
  return types;
}

export function toggleWorkspaceFocusGroup(
  filter: WorkspaceFocusFilter,
  groupId: WorkspaceFocusTypeGroupId
): WorkspaceFocusFilter {
  const next = new Set(
    filter.groupIds
      .map((id) => normalizeFocusGroupId(id))
      .filter((id): id is WorkspaceFocusTypeGroupId => id != null)
  );
  if (next.has(groupId)) next.delete(groupId);
  else next.add(groupId);
  const groupIds = WORKSPACE_FOCUS_TYPE_GROUPS.map((g) => g.id).filter((id) => next.has(id));
  return { ...filter, groupIds };
}

export function clearWorkspaceFocusFilter(
  current?: Pick<WorkspaceFocusFilter, "visibilityMode"> | null
): WorkspaceFocusFilter {
  return {
    ...EMPTY_WORKSPACE_FOCUS_FILTER,
    // Keep the user's dim/hide preference across clears.
    visibilityMode: current?.visibilityMode ?? "dim",
  };
}

function nodeMatchesQuery(
  node: WorkspaceNode,
  query: string,
  options?: {
    titles?: {
      report?: Record<string, string>;
      strategy?: Record<string, string>;
      simulation?: Record<string, string>;
    };
    enrichment?: WorkspaceFocusEnrichment;
  }
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const title = workspaceNodeTitle(node, {
    reportTitlesByEntityId: options?.titles?.report,
    strategyTitlesByEntityId: options?.titles?.strategy,
    simulationTitlesByEntityId: options?.titles?.simulation,
  }).toLowerCase();
  if (title.includes(q)) return true;

  const subtitle = node.meta?.subtitle?.trim().toLowerCase() ?? "";
  if (subtitle.includes(q)) return true;

  if (node.type === "report") {
    const metaLabel = node.meta?.reportTypeLabel?.trim().toLowerCase() ?? "";
    if (metaLabel && metaLabel.includes(q)) return true;
    const reportType = options?.enrichment?.reportTypeByEntityId[node.entityId] ?? node.meta?.reportType;
    if (reportType) {
      const key = normalizeReportTypeKey(reportType);
      const label = resolveReportTypeDisplayLabel(
        reportType,
        node.meta?.reportTypeLabel
      ).toLowerCase();
      if (key.includes(q) || label.includes(q)) return true;
    }
  }

  if (node.type === "policy" || node.type === "combined_policy") {
    if (policyNodeMatchesVersionQuery(node, q)) return true;
  }

  return false;
}

/** Match `v31` / `31` against policy version badges and combined source versions. */
function policyNodeMatchesVersionQuery(node: WorkspaceNode, q: string): boolean {
  const versions: number[] = [];
  const own = node.meta?.versionNumber;
  if (own != null && Number.isFinite(own)) versions.push(own);

  const sources = node.meta?.sourcePolicies;
  if (Array.isArray(sources)) {
    for (const source of sources) {
      if (source.versionNumber != null && Number.isFinite(source.versionNumber)) {
        versions.push(source.versionNumber);
      }
      const sourceTitle = source.title?.trim().toLowerCase() ?? "";
      if (sourceTitle && sourceTitle.includes(q)) return true;
    }
  }

  for (const version of versions) {
    const asNumber = String(version);
    const asLabel = `v${version}`;
    if (asNumber.includes(q) || asLabel.includes(q)) return true;
  }
  return false;
}

export type WorkspaceFocusMatchOptions = {
  titles?: {
    report?: Record<string, string>;
    strategy?: Record<string, string>;
    simulation?: Record<string, string>;
  };
  enrichment?: WorkspaceFocusEnrichment;
  edges?: WorkspaceEdge[];
  nodes?: WorkspaceNode[];
};

/**
 * True when the node itself is a focus *target* (seed) under the current filter.
 * Ancestors on the path to a seed are included separately via
 * {@link collectWorkspaceFocusVisibleNodeIds}.
 */
export function workspaceNodeMatchesFocusFilter(
  node: WorkspaceNode,
  filter: WorkspaceFocusFilter,
  options?: WorkspaceFocusMatchOptions
): boolean {
  if (!isWorkspaceFocusFilterActive(filter)) return true;

  const focusedTypes = typesForFocusGroups(filter.groupIds);
  const impliedTypes = new Set<WorkspaceNodeType>();
  if (filter.reportTypes.length > 0) impliedTypes.add("report");
  if (filter.simulationKind !== "any") {
    impliedTypes.add("simulation");
    impliedTypes.add("combined_simulation");
  }

  const effectiveTypes =
    focusedTypes.size > 0
      ? focusedTypes
      : impliedTypes.size > 0
        ? impliedTypes
        : null;

  if (effectiveTypes && !effectiveTypes.has(node.type)) return false;

  if (filter.statuses.length > 0) {
    const status = node.meta?.status;
    if (!status || !filter.statuses.includes(status)) return false;
  }

  if (filter.reportTypes.length > 0 && node.type === "report") {
    const raw =
      options?.enrichment?.reportTypeByEntityId[node.entityId] ?? node.meta?.reportType;
    if (!raw) return false;
    const key = normalizeReportTypeKey(raw);
    if (!filter.reportTypes.some((entry) => normalizeReportTypeKey(entry) === key)) {
      return false;
    }
  }

  if (
    filter.simulationKind !== "any" &&
    (node.type === "simulation" || node.type === "combined_simulation")
  ) {
    const isRerun = workspaceNodeIsSimulationRerun(
      node,
      options?.edges ?? [],
      options?.nodes ?? []
    );
    if (filter.simulationKind === "rerun" && !isRerun) return false;
    if (filter.simulationKind === "original" && isRerun) return false;
  }

  if (!nodeMatchesQuery(node, filter.query, options)) return false;

  return true;
}

/**
 * Nodes that should remain on the canvas when a focus filter is active:
 * every matching seed plus all ancestors on paths to those seeds.
 * Also keeps Strategy Sequential “executing account” bridge targets (and their
 * subtrees) so the right-hand arm does not vanish when focusing Policies.
 * Returns `null` when the filter is inactive (show everything).
 */
export function collectWorkspaceFocusVisibleNodeIds(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  filter: WorkspaceFocusFilter,
  options?: WorkspaceFocusMatchOptions
): Set<string> | null {
  if (!isWorkspaceFocusFilterActive(filter)) return null;

  const parentsByChild = new Map<string, string[]>();
  const childrenByParent = new Map<string, string[]>();
  for (const edge of edges) {
    const parents = parentsByChild.get(edge.targetNodeId) ?? [];
    if (!parents.includes(edge.sourceNodeId)) parents.push(edge.sourceNodeId);
    parentsByChild.set(edge.targetNodeId, parents);

    const children = childrenByParent.get(edge.sourceNodeId) ?? [];
    if (!children.includes(edge.targetNodeId)) children.push(edge.targetNodeId);
    childrenByParent.set(edge.sourceNodeId, children);
  }

  const matchOptions: WorkspaceFocusMatchOptions = {
    ...options,
    edges: options?.edges ?? edges,
    nodes: options?.nodes ?? nodes,
  };

  const visible = new Set<string>();
  for (const node of nodes) {
    if (!workspaceNodeMatchesFocusFilter(node, filter, matchOptions)) continue;
    // Walk every parent path (combined_policy may have multiple parents).
    const stack = [node.id];
    const seen = new Set<string>();
    while (stack.length > 0) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      visible.add(id);
      for (const parentId of parentsByChild.get(id) ?? []) {
        stack.push(parentId);
      }
    }
  }

  expandVisibleWithExecutingAccountBridges(visible, nodes, edges, childrenByParent);

  return visible;
}

/**
 * Sequential Strategy layout bridges policy → fed account. Keep those hops (and
 * the account subtree) when the source policy is already on the focus path.
 */
function expandVisibleWithExecutingAccountBridges(
  visible: Set<string>,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  childrenByParent: Map<string, string[]>
): void {
  const bridges = listExecutingAccountBridges(nodes, edges);
  if (bridges.length === 0) return;

  const addSubtree = (rootId: string): void => {
    const stack = [rootId];
    const seen = new Set<string>();
    while (stack.length > 0) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      visible.add(id);
      for (const childId of childrenByParent.get(id) ?? []) {
        stack.push(childId);
      }
    }
  };

  // Multi-hop: a newly included account may host policies that bridge further.
  let grew = true;
  while (grew) {
    grew = false;
    for (const bridge of bridges) {
      if (!visible.has(bridge.fromPolicyNodeId)) continue;
      if (visible.has(bridge.toAccountNodeId)) continue;
      addSubtree(bridge.toAccountNodeId);
      grew = true;
    }
  }
}

/** Collect status / report-type options present on the current canvas. */
export function collectWorkspaceFocusFilterOptions(
  nodes: WorkspaceNode[],
  enrichment: WorkspaceFocusEnrichment
): {
  statuses: WorkspaceNodeStatus[];
  reportTypes: Array<{ value: string; label: string }>;
} {
  const statusSet = new Set<WorkspaceNodeStatus>();
  const reportTypeSet = new Set<string>();

  for (const node of nodes) {
    if (node.meta?.status) statusSet.add(node.meta.status);
    if (node.type === "report") {
      const raw = enrichment.reportTypeByEntityId[node.entityId];
      if (raw?.trim()) reportTypeSet.add(normalizeReportTypeKey(raw));
    }
  }

  // Quiet success statuses (saved/published) are not useful as filter options.
  const statusOrder: WorkspaceNodeStatus[] = [
    "processing",
    "complete",
    "failed",
    "draft",
    "archived",
  ];
  const statuses = statusOrder.filter((s) => statusSet.has(s));

  return {
    statuses,
    reportTypes: Array.from(reportTypeSet)
      .sort()
      .map((value) => {
        const nodeWithType = nodes.find(
          (n) =>
            n.type === "report" &&
            normalizeReportTypeKey(enrichment.reportTypeByEntityId[n.entityId] ?? "") === value
        );
        return {
          value,
          label: resolveReportTypeDisplayLabel(
            value,
            nodeWithType?.meta?.reportTypeLabel
          ),
        };
      }),
  };
}
